/**
 * Theme preference. "system" follows the OS until the person chooses otherwise;
 * an explicit choice persists. Stored in localStorage because it must be read
 * synchronously before first paint (see the inline script in index.html).
 */

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_KEY = 'mytxt.theme';

export function parsePreference(value: unknown): ThemePreference {
  return value === 'light' || value === 'dark' ? value : 'system';
}

export function resolveTheme(preference: ThemePreference, systemDark: boolean): ResolvedTheme {
  if (preference === 'system') return systemDark ? 'dark' : 'light';
  return preference;
}

export function loadPreference(storage: Pick<Storage, 'getItem'> | undefined = globalThis.localStorage): ThemePreference {
  try {
    return parsePreference(storage?.getItem(THEME_KEY));
  } catch {
    return 'system';
  }
}

export function savePreference(preference: ThemePreference, storage: Pick<Storage, 'setItem' | 'removeItem'> | undefined = globalThis.localStorage): void {
  try {
    if (preference === 'system') storage?.removeItem(THEME_KEY);
    else storage?.setItem(THEME_KEY, preference);
  } catch {
    // Theme is a convenience; failing to remember it is harmless.
  }
}

export function systemPrefersDark(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches;
}

export function prefersReducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const THEME_COLOR: Record<ResolvedTheme, string> = { light: '#ecebe7', dark: '#0d0e10' };

export function applyTheme(theme: ResolvedTheme, animate: boolean): void {
  const root = document.documentElement;
  if (root.dataset.theme === theme) return;
  const commit = () => {
    root.dataset.theme = theme;
    root.style.colorScheme = theme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[theme]);
  };
  const doc = document as Document & { startViewTransition?: (cb: () => void) => unknown };
  if (animate && doc.startViewTransition && !prefersReducedMotion()) {
    root.dataset.themeTransition = '';
    const transition = doc.startViewTransition(commit) as { finished?: Promise<void> };
    transition.finished?.finally(() => delete root.dataset.themeTransition);
  } else {
    commit();
  }
}
